const DEV = process.env.NODE_ENV !== 'production';
const LIVE_FEED = 'https://politics.api.cnn.io/results/national-races/2026-HG.json';
const LIVE_URLS = DEV ? ['/house-api/2026', LIVE_FEED] : [LIVE_FEED];
// The national House race, for the overall share of the expected vote counted.
const NATIONAL_FEED = 'https://politics.api.cnn.io/results/race/2026-HG-US.json';
const NATIONAL_URLS = DEV ? ['/house-us-api/2026', NATIONAL_FEED] : [NATIONAL_FEED];

// Louisiana's Nov. 3 House races are open primaries; top-two runoffs are Dec. 12.
const RUNOFF_DATES = { LA: 'Dec. 12' };

function majorParty(candidate) {
  const code = candidate.majorParty || candidate.candidatePartyCode || '';
  if (code === 'DEM' || code === 'D') return 'D';
  if (code === 'REP' || code === 'R') return 'R';
  return null;
}

function colorKey(candidate) {
  const code = candidate.majorParty || candidate.candidatePartyCode || '';
  const major = majorParty(candidate);
  if (major) return major;
  if (code === 'GRN' || code === 'GRE' || code === 'G') return 'G';
  if (code === 'IND' || code === 'I') return 'I';
  if (code === 'LIB' || code === 'L') return 'L';
  return null;
}

function hash(value) {
  let total = 2166136261;
  const text = String(value);
  for (let i = 0; i < text.length; i += 1) {
    total = Math.imul(total ^ text.charCodeAt(i), 16777619);
  }
  return ((total >>> 0) % 1000) / 1000;
}

// Fewer races than this means a broken feed; keep showing the last good data.
const MIN_RACES = 400;

export function normalizeFeed(races) {
  if (!Array.isArray(races)) throw new Error('House feed was not a list of races');
  const general = races.filter(race => race && race.electionType === 'G');
  const byId = new Map();
  general.forEach((race) => {
    // One malformed race is skipped rather than rejecting the whole feed.
    let seat;
    try {
      seat = normalizeRace(race);
    } catch (error) {
      console.warn(`Skipped a malformed race (${race.stateAbbreviation}-${race.jurisdictionCode}):`, error.message);
      return;
    }
    const seen = byId.get(seat.id);
    if (!seen || (seat.extracted || '') > (seen.extracted || '')) byId.set(seat.id, seat);
  });
  // Counted after duplicates are removed, so a duplicate can't hide a missing race.
  if (byId.size < MIN_RACES) {
    throw new Error(`Expected 435 House general elections and received ${byId.size}`);
  }
  // Races are matched to seats by ID, so an extra or missing race only affects that seat.
  if (byId.size !== 435) {
    console.warn(`Expected 435 House general elections and received ${byId.size}`);
  }
  return [...byId.values()];
}

const isWriteInTotal = candidate => /^total write-ins?$/i
  .test((candidate.fullName || candidate.lastName || '').trim());

export function normalizeRace(race) {
  {
    // "Total Write-ins" is an aggregate row, not a candidate, so it can't lead
    // or appear in the district card. Named write-in candidates stay.
    const candidates = (race.candidates || []).filter(candidate => !isWriteInTotal(candidate)).map(candidate => ({
      party: majorParty(candidate),
      color: colorKey(candidate),
      name: candidate.fullName || [candidate.firstName, candidate.lastName].filter(Boolean).join(' '),
      votes: candidate.voteNum || 0,
      pct: candidate.votePercentStr || null,
      winner: !!candidate.winner,
      runoff: !!candidate.runoffCandidate,
      // The feed's spelled-out party, e.g. "No Party Affiliation" for code NF.
      partyName: candidate.partyName || null,
    })).sort((a, b) => b.votes - a.votes);

    // Independents and third parties count as 'O'; otherColor keeps their own color.
    const side = candidate => candidate.party || 'O';
    const called = race.editorialStatus === 'called' || race.status === 'W';
    const winner = called ? candidates.find(candidate => candidate.winner) : null;
    // A race headed to a runoff has no winner yet. If both runoff candidates
    // are from the same party, that party is guaranteed the seat.
    const finalists = candidates.filter(candidate => candidate.runoff);
    const runoff = !winner && (race.status === 'V' || (called && finalists.length > 1));
    const finalistSides = [...new Set(finalists.map(side))];
    const runoffParty = runoff && finalists.length > 1 && finalistSides.length === 1
      && finalistSides[0] !== 'O' ? finalistSides[0] : null;
    const tied = candidates.length > 1 && candidates[0].votes > 0
      && candidates[0].votes === candidates[1].votes;
    const leader = !winner && !runoff && !tied && candidates.length && candidates[0].votes > 0
      ? candidates[0] : null;
    const decided = winner || leader;

    // At-large districts are 1 in the 2024 baseline; guard against a feed using 0.
    const district = Number(race.jurisdictionCode) || 1;
    return {
      state: race.stateAbbreviation,
      stateName: race.stateName,
      district,
      id: `${race.stateAbbreviation}-${district}`,
      called: !!winner || !!runoffParty,
      winner: winner ? side(winner) : runoffParty,
      leader: leader ? side(leader) : null,
      otherColor: decided && !decided.party ? decided.color : null,
      otherName: decided && !decided.party ? decided.partyName : null,
      runoff,
      runoffDate: runoff ? RUNOFF_DATES[race.stateAbbreviation] || null : null,
      isSwitch: race.isSwitch === true,
      updated: race.voteTimestamp || null,
      extracted: race.extractedAt || null,
      pctReporting: race.percentReporting == null ? null : race.percentReporting,
      candidates,
    };
  }
}

export function joinSeats(baseline, live, editorial) {
  const liveById = {};
  live.forEach((seat) => {
    liveById[seat.id] = seat;
  });
  const stateByCode = {};
  editorial.states.forEach((state) => {
    stateByCode[state.code] = state;
  });

  return baseline.map((previous) => {
    const current = liveById[previous.id] || {
      called: false,
      winner: null,
      leader: null,
      runoff: false,
      pctReporting: null,
      candidates: [],
    };
    const state = stateByCode[previous.state];
    const targeted = !!(state && state.targeted.some(item => item.district === previous.district));
    const called = !!current.called;
    const winner = current.winner || null;
    const leader = called ? null : (current.leader || null);

    return {
      id: previous.id,
      state: previous.state,
      stateName: state ? state.name : previous.stateName,
      area: (editorial.areas && editorial.areas[previous.id]) || null,
      district: previous.district,
      prevHolder: previous.winner,
      called,
      winner,
      leader,
      shown: called ? winner : leader,
      isLeading: !called && !!leader,
      flipped: called && !!winner && winner !== previous.winner,
      runoff: !!current.runoff,
      runoffDate: current.runoffDate || null,
      otherColor: current.otherColor || null,
      otherName: current.otherName || null,
      isSwitch: !!current.isSwitch,
      pctReporting: current.pctReporting,
      updated: current.updated || null,
      candidates: current.candidates || [],
      newMap: !!state,
      targeted,
      seeker: state ? state.seeker : null,
      drawnBy: state ? state.drawnBy : '',
      margins: (editorial.margins && editorial.margins[previous.id]) || null,
    };
  });
}

// Seats headed to a runoff are as final as election night gets.
export function resultMode(seats) {
  if (!seats.some(seat => seat.called || seat.leader || seat.runoff)) return 'none';
  if (seats.every(seat => seat.called || seat.runoff)) return 'full';
  return 'partial';
}

function otherParty(party) {
  return party === 'D' ? 'R' : 'D';
}

const MOCK_CLOCK = Date.parse('2026-11-04T10:00:00Z');

// Mock results also need matching candidates so the district cards agree
// with the bar: a flagged winner when called, and no votes when unreported.
function syncCandidates(seat) {
  if (!seat.shown) {
    return {
      ...seat,
      pctReporting: null,
      candidates: seat.candidates.map(candidate => ({
        ...candidate, votes: 0, pct: null, winner: false,
      })),
    };
  }
  const counted = seat.candidates.some(candidate => candidate.votes > 0);
  const source = counted ? seat.candidates : mockVotes(seat);
  let marked = false;
  const candidates = source.map((candidate) => {
    const winner = seat.called && !marked && candidate.party === seat.winner;
    if (winner) marked = true;
    return { ...candidate, winner };
  });
  if (counted) return { ...seat, candidates };
  const roll = hash(`${seat.id}-in`);
  const pctReporting = seat.called ? (roll < 0.5 ? 100 : 99) : Math.round(20 + (roll * 70));
  const updated = seat.updated || new Date(MOCK_CLOCK - (roll * 3 * 3600 * 1000)).toISOString();
  return {
    ...seat, candidates, pctReporting, updated,
  };
}

// Mock vote shares for when the feed has no votes yet, so the shown party is on top.
function mockVotes(seat) {
  const roll = hash(`${seat.id}-votes`);
  const top = seat.called ? 52 + (roll * 18) : 50.5 + (roll * 7);
  const leaderIndex = seat.candidates.findIndex(candidate => candidate.party === seat.shown);
  const runnerIndex = seat.candidates.findIndex((candidate, index) => (
    index !== leaderIndex && (candidate.party === 'D' || candidate.party === 'R')
  ));
  const minors = seat.candidates.length - (leaderIndex >= 0 ? 1 : 0) - (runnerIndex >= 0 ? 1 : 0);
  const minorShare = 1.5;
  const shares = seat.candidates.map((candidate, index) => {
    if (index === leaderIndex) return top;
    if (index === runnerIndex) return 100 - top - (minors * minorShare);
    return minorShare;
  });
  return seat.candidates
    .map((candidate, index) => ({
      ...candidate,
      votes: Math.round(shares[index] * 1000),
      pct: shares[index].toFixed(1),
    }))
    .sort((a, b) => b.votes - a.votes);
}

// Mock stages start from a blank slate so every load and poll shows the same
// outcomes; only candidate names and parties come from the live feed.
function clearResults(seat) {
  return {
    ...seat,
    called: false,
    winner: null,
    leader: null,
    shown: null,
    isLeading: false,
    flipped: false,
    runoff: false,
    runoffDate: null,
    otherColor: null,
    otherName: null,
    pctReporting: null,
    updated: null,
    candidates: seat.candidates.map(candidate => ({
      ...candidate, votes: 0, pct: null, winner: false,
    })),
  };
}

export function applyMode(seats, mode, keepLive = false) {
  if (!mode) return seats;
  const start = keepLive ? seats : seats.map(clearResults);
  return simulate(start, mode).map(syncCandidates);
}

function simulate(seats, mode) {
  if (mode === 'none') {
    return seats.map(seat => ({
      ...seat,
      called: false,
      winner: null,
      leader: null,
      shown: null,
      isLeading: false,
      flipped: false,
    }));
  }
  if (mode === 'full') {
    return seats.map((seat) => {
      const flips = seat.targeted && seat.seeker && hash(seat.id) < 0.65;
      const winner = seat.winner || seat.leader || (flips ? seat.seeker : seat.prevHolder);
      return {
        ...seat,
        called: true,
        winner,
        leader: null,
        shown: winner,
        isLeading: false,
        flipped: winner !== seat.prevHolder,
      };
    });
  }

  if (mode === 'early') {
    return seats.map((seat) => {
      const roll = hash(seat.id);
      if (roll < 0.06) {
        const winner = seat.targeted && seat.seeker && roll < 0.03 ? seat.seeker : seat.prevHolder;
        return {
          ...seat,
          called: true,
          winner,
          leader: null,
          shown: winner,
          isLeading: false,
          flipped: winner !== seat.prevHolder,
        };
      }
      if (roll < 0.16) {
        const leader = roll < 0.1 ? otherParty(seat.prevHolder) : seat.prevHolder;
        return {
          ...seat, called: false, winner: null, leader, shown: leader, isLeading: true, flipped: false,
        };
      }
      return {
        ...seat, called: false, winner: null, leader: null, shown: null, isLeading: false, flipped: false,
      };
    });
  }

  return seats.map((seat) => {
    const roll = hash(seat.id);
    if (roll < 0.55) {
      const winner = seat.targeted && seat.seeker && roll < 0.28
        ? seat.seeker
        : (seat.winner || seat.leader || seat.prevHolder);
      return {
        ...seat,
        called: true,
        winner,
        leader: null,
        shown: winner,
        isLeading: false,
        flipped: winner !== seat.prevHolder,
      };
    }
    const leader = roll < 0.8 ? otherParty(seat.prevHolder) : seat.prevHolder;
    return {
      ...seat,
      called: false,
      winner: null,
      leader,
      shown: leader,
      isLeading: true,
      flipped: false,
    };
  });
}

// Louisiana test scenarios for the ?results= preview. Each race is built in the
// raw feed format and run through normalizeRace, so runoffs and third-party
// results exercise the same parsing as election night.
const LA_SCENARIOS = [
  { key: 'none', label: 'No votes yet', top: [] },
  { key: 'leadD', label: 'Dem. leading', top: ['D', 'R'] },
  { key: 'leadR', label: 'GOP leading', top: ['R', 'D'] },
  { key: 'winD', label: 'Dem. wins outright', top: ['D', 'R'] },
  { key: 'winR', label: 'GOP wins outright', top: ['R', 'D'] },
  {
    key: 'runoffDR', label: 'Dem. vs. GOP runoff', top: ['D', 'R'], runoff: true,
  },
  {
    key: 'runoffRD', label: 'GOP vs. Dem. runoff', top: ['R', 'D'], runoff: true,
  },
  {
    key: 'runoffRR', label: 'GOP vs. GOP runoff', top: ['R', 'R'], runoff: true,
  },
  {
    key: 'runoffDD', label: 'Dem. vs. Dem. runoff', top: ['D', 'D'], runoff: true,
  },
  {
    key: 'runoffOR', label: 'Third-party vs. GOP runoff', top: ['O', 'R'], runoff: true,
  },
  { key: 'leadO', label: 'Third-party leading', top: ['O', 'D'] },
  { key: 'winO', label: 'Third-party wins outright', top: ['O', 'R'] },
  { key: 'tie', label: 'Tied vote', top: ['D', 'R'] },
];

const SAMPLE_NAMES = { D: 'Democrat', R: 'Republican', O: 'Independent' };

function seeded(seed) {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6D2B79F5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function testRace(seat, scenario, rand) {
  const pool = seat.candidates.map(candidate => ({ ...candidate, votes: 0 }));
  const sideOf = candidate => candidate.party || 'O';
  const take = (side, used) => {
    let pick = pool.find(candidate => sideOf(candidate) === side && !used.includes(candidate));
    if (!pick) {
      const count = pool.filter(candidate => sideOf(candidate) === side).length + 1;
      pick = {
        party: side === 'O' ? null : side,
        color: side === 'O' ? 'I' : side,
        name: `Sample ${SAMPLE_NAMES[side]} ${count}`,
        votes: 0,
      };
      pool.push(pick);
    }
    used.push(pick);
    return pick;
  };
  const top = [];
  scenario.top.forEach(side => take(side, top));

  const between = (low, high) => low + (rand() * (high - low));
  let shares = [];
  if (scenario.key === 'tie') shares = [44, 44];
  else if (scenario.runoff) shares = [between(32, 45), between(20, 30)];
  else if (scenario.key.startsWith('win')) shares = [between(52, 65), between(25, 40)];
  else if (scenario.key.startsWith('lead')) shares = [between(42, 49), between(35, 41)];
  const others = pool.filter(candidate => !top.includes(candidate));
  const rest = Math.max(0, 100 - shares.reduce((sum, value) => sum + value, 0));
  const total = 180000;
  const voted = scenario.top.length > 0;
  top.forEach((candidate, index) => {
    candidate.votes = Math.round(total * (shares[index] / 100));
  });
  others.forEach((candidate, index) => {
    // Split what's left, each smaller than the runner-up.
    const share = voted ? Math.min(shares[1] - 1, (rest / others.length) * (1 + ((others.length - index) / others.length))) : 0;
    candidate.votes = Math.max(0, Math.round(total * (share / 100)));
  });
  if (scenario.key === 'tie') top[1].votes = top[0].votes;
  const sum = pool.reduce((acc, candidate) => acc + candidate.votes, 0) || 1;

  const called = scenario.key.startsWith('win') || scenario.runoff;
  const code = (candidate) => {
    if (candidate.party) return candidate.party;
    return candidate.color || 'NPA';
  };
  return {
    electionType: 'G',
    stateAbbreviation: seat.state,
    stateName: seat.stateName || 'Louisiana',
    jurisdictionCode: seat.district,
    editorialStatus: called ? 'called' : 'voting',
    status: (scenario.runoff && 'V') || (called && 'W') || 'U',
    percentReporting: voted ? Math.round(called ? between(95, 99) : between(25, 80)) : 0,
    voteTimestamp: voted ? new Date(MOCK_CLOCK - (rand() * 3600 * 1000)).toISOString() : null,
    isSwitch: false,
    candidates: pool.map(candidate => ({
      majorParty: { D: 'DEM', R: 'REP' }[candidate.party] || null,
      candidatePartyCode: code(candidate),
      fullName: candidate.name,
      partyName: candidate.partyName || (candidate.party ? null : 'Independent'),
      voteNum: candidate.votes,
      votePercentStr: voted ? ((candidate.votes / sum) * 100).toFixed(1) : null,
      winner: called && !scenario.runoff && candidate === top[0],
      runoffCandidate: !!scenario.runoff && top.includes(candidate),
    })),
  };
}

// Gives each Louisiana race a different scenario, with at least one runoff.
// The same seed always gives the same results, so a scenario survives each poll.
export function louisianaScenario(live, seed) {
  const rand = seeded(seed);
  const order = LA_SCENARIOS.slice();
  for (let i = order.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  const districts = live.filter(seat => seat.state === 'LA').length;
  if (!order.slice(0, districts).some(scenario => scenario.runoff)) {
    const runoff = order.findIndex(scenario => scenario.runoff);
    [order[0], order[runoff]] = [order[runoff], order[0]];
  }
  const picks = [];
  const races = live.map((seat) => {
    if (seat.state !== 'LA') return seat;
    const scenario = order[picks.length % order.length];
    picks.push({ id: seat.id, label: scenario.label });
    return normalizeRace(testRace(seat, scenario, rand));
  });
  return { live: races, picks };
}

export function urlParam(name) {
  let { search } = window.location;
  try {
    if (window.parent && window.parent !== window && window.parent.location.search) {
      search = window.parent.location.search;
    }
  } catch (error) {
    search = window.location.search;
  }
  return new URLSearchParams(search).get(name);
}

// Test results (?results=none|early|partial|full, and &feed=live with them) only on the
// dev server: staged and published builds define PRODUCTION and always show the feed.
// eslint-disable-next-line no-undef
const TEST_PARAMS = typeof PRODUCTION === 'undefined' || !PRODUCTION;

export function resultsOverride() {
  if (!TEST_PARAMS) return null;
  const value = urlParam('results');
  if (!value) return null;
  const mode = value.toLowerCase();
  if (mode === 'none' || mode === 'early' || mode === 'partial' || mode === 'full') return mode;
  return null;
}

export function keepLiveResults() {
  return TEST_PARAMS && urlParam('feed') === 'live';
}

const FEED_TIMEOUT_MS = 15000;

async function fetchRaces(url) {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), FEED_TIMEOUT_MS) : null;
  try {
    const response = await fetch(url, { cache: 'no-store', signal: controller ? controller.signal : undefined });
    if (!response.ok) throw new Error(`House feed responded ${response.status}`);
    return normalizeFeed(await response.json());
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function fetchFirst(urls) {
  let lastError = null;
  for (let i = 0; i < urls.length; i += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fetchRaces(urls[i]);
    } catch (error) {
      lastError = error;
      console.warn(`House feed failed at ${urls[i]}:`, error.message);
    }
  }
  throw lastError;
}

export function loadLive() {
  return fetchFirst(LIVE_URLS);
}

export function normalizeNational(race) {
  const pct = race && race.percentReporting != null ? Number(race.percentReporting) : null;
  return {
    pctReporting: pct == null || Number.isNaN(pct) ? null : pct,
    extracted: (race && race.extractedAt) || null,
  };
}

// Optional: if this feed fails, the graphic keeps its last value and the
// seat results are unaffected.
export async function loadNational() {
  for (let i = 0; i < NATIONAL_URLS.length; i += 1) {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), FEED_TIMEOUT_MS) : null;
    try {
      // eslint-disable-next-line no-await-in-loop
      const response = await fetch(NATIONAL_URLS[i], { cache: 'no-store', signal: controller ? controller.signal : undefined });
      if (!response.ok) throw new Error(`National House race responded ${response.status}`);
      // eslint-disable-next-line no-await-in-loop
      return normalizeNational(await response.json());
    } catch (error) {
      console.warn(`National House race failed at ${NATIONAL_URLS[i]}:`, error.message);
    } finally {
      if (timer) clearTimeout(timer);
    }
  }
  return null;
}
